import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import Button from '@/main/components/shared/button/button';

import { clearAllCart, removeCart, useCartReducer } from '@/main/store/cart';
import {
  getTranslateByKey,
  isNullUndefined,
  isShowControlByElementId
} from '@/main/services/utils';
import { formatCurrency } from '@/main/services/utilsNumber';
import InfoProduct from '../../../components/infoProduct/infoProduct';
import * as S from './styles';
import { useCreateOrderContext } from '../../../context';
import {
  IPartsCard,
  ISupplierCard
} from '@/main/components/shared/card/cardShopping';
import {
  SkeletonLoading,
  SpinnerLoading
} from '@/main/components/shared/styles';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { useSearchParams } from 'react-router-dom';
import { Controls } from '@/main/services/constants/constantsControl';
import { userIsAdmin } from '@/main/services/utilsUser';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';

interface IModalCartProps {
  handleFinishOrder?: () => void;
  handleAddCart: (
    part: IPartsCard,
    supplier: ISupplierCard,
    quantityUpdate: number
  ) => void;
}

function BodyModalCart({ handleAddCart }: IModalCartProps) {
  const dispatch = useDispatch();
  const cartReducer = useCartReducer();
  const cartItems = cartReducer.cartItems.data;
  const [deliveryBySupplier, setDeliveryBySupplier] = useState<boolean>(false);
  const showPrices = isShowControlByElementId(Controls.IdShowPrices);

  useEffect(() => {
    const allDeliveriesBySupplier = cartItems.every(
      item => item.deliveryBySupplier === true
    );
    setDeliveryBySupplier(allDeliveriesBySupplier);
  }, [cartItems]);

  function callDeletePart(guidQuoteSupplierItem: string, idPart: string) {
    dispatch(removeCart(guidQuoteSupplierItem, idPart, false, true, []));
  }

  function callUpdatePart(
    guidSupplier: string,
    guidQuoteSupplierItem: string,
    idPartType: number,
    count: number
  ) {
    const findSupplier = cartItems.find(item => item.guid === guidSupplier);
    if (!findSupplier)
      throw new Error(getTranslateByKey('information.notFoundSupplier', true));
    const findPart = findSupplier.parts.find(
      item =>
        item.guidQuoteSupplierItem === guidQuoteSupplierItem &&
        item.idPartType === idPartType
    );
    if (!findPart)
      throw new Error(getTranslateByKey('information.notFoundPart', true));
    handleAddCart(findPart as any, findSupplier, count);
  }

  if (cartReducer.cartItems.isLoading)
    return (
      <S.ContainerBodyModal>
        <SpinnerLoading size="medium" />
      </S.ContainerBodyModal>
    );
  if (!cartReducer.cartItems.data || cartReducer.cartItems.data.length === 0)
    return (
      <S.ContainerBodyModal>
        <ResultNotFoundComponent />
      </S.ContainerBodyModal>
    );

  const totalPriceCart = cartReducer.summary.total;
  const totalShippingCart = cartReducer.summary.shippingCost;
  const cartItemsOrder = cartItems;
  const showShippingCostPrice = userIsAdmin() && !deliveryBySupplier;

  const isRemoving = (idPart: string) =>
    cartReducer.cartItems.idPartIsLoading === idPart &&
    cartReducer.cartItems.isLoadingUpdate;
  const isLoading =
    cartReducer.cartItems.isLoadingUpdate || cartReducer.cartItems.isLoading;
  return (
    <S.ContainerBodyModal>
      <S.AreaProducts>
        {cartItemsOrder.map(cart => (
          <S.BoxProductSupplier key={cart.guid}>
            <S.TextoInfoProduct>{cart.name}</S.TextoInfoProduct>
            {cart.parts
              .sort((a, b) => {
                const aPartName = !isNullUndefined(
                  a.quoteProcurementPartInfo?.partName
                )
                  ? a.quoteProcurementPartInfo?.partName
                  : '';
                const bPartName = !isNullUndefined(
                  b.quoteProcurementPartInfo?.partName
                )
                  ? b.quoteProcurementPartInfo?.partName
                  : '';

                if (aPartName < bPartName) return -1;
                if (aPartName > bPartName) return 1;
                return 0;
              })
              .map(item => (
                <InfoProduct
                  key={item.id}
                  id={item.id}
                  isPaintProject={item.idPartType === PartTypeEnum.Paint}
                  guidQuoteSupplierItem={String(item.guidQuoteSupplierItem)}
                  guidSupplierInventoryWarehouse={
                    item.guidSupplierInventoryWarehouse
                  }
                  guidSupplier={cart.guid}
                  image={item.image}
                  idPartType={item.idPartType}
                  price={item.price}
                  quantity={item.quantity}
                  stock={Number(item.stock)}
                  isLoading={cartReducer.cartItems.isLoadingUpdate}
                  priceCap={item.priceCap}
                  priceCapFormatted={item.priceCapFormatted}
                  priceDiffPercent={item.priceDiffPercent}
                  isRemoving={isRemoving(item.id)}
                  onClickDelete={callDeletePart}
                  onClickDecrement={callUpdatePart}
                  onClickIncrement={callUpdatePart}
                  quoteProcurementPartInfo={item.quoteProcurementPartInfo}
                />
              ))}
          </S.BoxProductSupplier>
        ))}
      </S.AreaProducts>

      <S.SummaryCart>
        <p>{getTranslateByKey('quotation.shippingCost')}</p>
        <p>
          {isLoading ? (
            <SkeletonLoading $width="50px" />
          ) : (
            <S.AreaFreeShipping>
              <S.TextFreeShipping>
                {getTranslateByKey('information.shippingIncluded')}
              </S.TextFreeShipping>
              {showShippingCostPrice && (
                <S.PriceFreeShipping>
                  {`(${formatCurrency(totalShippingCart)})`}
                </S.PriceFreeShipping>
              )}
            </S.AreaFreeShipping>
          )}
        </p>
        {showPrices && (
          <>
            <p>{getTranslateByKey('information.total')}</p>
            <p>
              {isLoading ? (
                <SkeletonLoading $width="80px" />
              ) : (
                formatCurrency(totalPriceCart)
              )}
            </p>
          </>
        )}
      </S.SummaryCart>
    </S.ContainerBodyModal>
  );
}

function ModalCart({ handleFinishOrder, handleAddCart }: IModalCartProps) {
  const dispatch = useDispatch();
  const cartReducer = useCartReducer();
  const [searchParams] = useSearchParams();
  const guidQuote = searchParams.get('guidQuote');
  const cartItems = cartReducer.cartItems.data;

  const { integration, isQuoteOpen, isQuoteManual } = useCreateOrderContext();

  const { displayModalCancel } = useCreateOrderContext();

  function handleClearCart() {
    if (guidQuote) {
      dispatch(clearAllCart(String(guidQuote)));
    }
  }
  const isLoadingCart = cartReducer.cartItems.isLoading;
  return (
    <S.Container id='id-general-modalCart'>
      <h4>{getTranslateByKey('information.cart')}</h4>
      <BodyModalCart handleAddCart={handleAddCart} />
      <S.FooterButtons id='id-createOrder-footerButtons'>
        <Button
          type="button"
          onClick={handleFinishOrder}
          id="id-createOrder-button-finishOrder"
          disabled={(integration && isQuoteOpen) || cartItems.length === 0}>
          {getTranslateByKey('quotation.finishOrder')}
        </Button>
        <SecondaryButton
          type="button"
          onClick={handleClearCart}
          id="id-createOrder-button-clearCart"
          disabled={
            (integration && isQuoteOpen) ||
            (integration && isQuoteManual) ||
            isLoadingCart
          }>
          {getTranslateByKey('cart.clear')}
        </SecondaryButton>
        <SecondaryButton
          type="button"
          onClick={displayModalCancel}
          id="id-createOrder-button-cancel"
          disabled={integration && isQuoteOpen}>
          {getTranslateByKey('information.cancel')}
        </SecondaryButton>
      </S.FooterButtons>
    </S.Container>
  );
}

export default ModalCart;
